import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import { RecentWritingCardLesson } from 'teacher-ui-client/components/writing-legends/recent-writing-card-lesson'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type RecentWritingService from 'teacher-ui-client/services/recent-writing'
import { joinQueryParams } from 'teacher-ui-client/utils/uri'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'
import { ReflectionCounter } from 'teacher-ui-client/components/writing-legends/reflection-counter'
import { concat } from '@ember/helper'
import { LessionGuideData } from 'teacher-ui-client/components/tables/lesson-guide-table/writing'
import type { LessonGuideDataItem } from 'teacher-ui-client/components/tables/lesson-guide-table/writing'
import { sumBy } from 'lodash'

export interface RecentWritingPlacementItem {
  student_id: string
  blake_student_id: number
  first_name: string
  last_name: string
  avatar: string
  student_answers: string[]
  activity_id: string
  activity_title?: string
  lesson_result_id?: number
  content_data_name?: string
  attempt_count: number
  is_passed?: boolean
  created_date: string
  map_number?: number
  lesson_number?: number
  classroom_name?: string
  is_skipped: boolean
  writing_mood?: string
}

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
    pageNumber: number
    perPage: number
  }
}

export interface RecentWritingPlacementData {
  aggregate: {
    map_placements: {
      map: number
      student_count: number
      grade_position?: number
    }[]
    moods: {
      low?: number
      medium?: number
      high?: number
    }
  }
  writing: RecentWritingPlacementItem[]
  total: number
}

type PlacementResultsItem = Pick<LessonGuideDataItem, 'maps'> & {
  gradePositions: string
  totalStudents: number
}

export class RecentWritingsListPlacement extends Component<Signature> {
  @service intl!: IntlService

  @service gradeSets!: GradeSetsService

  @service recentWriting!: RecentWritingService

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const queryPeriod = this.recentWriting.buildQueryPeriod(this.args.period)

    const url = this.recentWriting.buildUrl('placement_writing')
    const joinedUrl = joinQueryParams(url, {
      fromDate: queryPeriod.start,
      toDate: queryPeriod.end,
      take: this.args.perPage,
      skip: (this.args.pageNumber - 1) * this.args.perPage,
      clId: requestScope.scope === 'school-class' ? requestScope.id : undefined,
      tId: requestScope.scope === 'teacher' ? requestScope.id : undefined,
    })

    return this.recentWriting.fetchData<RecentWritingPlacementData>(joinedUrl.href)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get recentPlacementWritings() {
    return this.latestData?.writing.map((writing) => {
      return {
        ...writing,
        student_answers: writing.is_skipped
          ? [this.intl.t('pages.recentWriting.placementTest.skipMessage', { lessonNumber: writing.lesson_number })]
          : writing.student_answers,
      }
    })
  }

  get reflectionCounts() {
    const moods = this.latestData?.aggregate.moods
    return {
      smile: moods?.high ?? 0,
      unsure: moods?.medium ?? 0,
      disappointed: moods?.low ?? 0,
    }
  }

  get totalPages() {
    return Math.ceil((this.latestData?.total ?? 0) / this.args.perPage)
  }

  get placementResultsTable() {
    // exclude the last item for now as the students are not placed on higher maps yet
    const guideData = LessionGuideData.slice(0, -1)
    const placementResults: PlacementResultsItem[] = guideData.map(({ gradePositions, maps }: LessonGuideDataItem) => {
      const mapPlacements = this.latestData?.aggregate.map_placements.filter(
        ({ map }) => maps.from <= map && maps.to >= map,
      )
      return {
        gradePositions: `${this.gradeSets.nameFromGradeSetByPosition(
          gradePositions.from,
        )}–${this.gradeSets.nameFromGradeSetByPosition(gradePositions.to)}`,
        maps,
        totalStudents: mapPlacements ? sumBy(mapPlacements, (placement) => placement.student_count) : 0,
      }
    })

    return [
      {
        title: this.intl.t('pages.recentWriting.placementTest.table.headers.year'),
        data: placementResults.map((d) => d.gradePositions),
      },
      {
        title: this.intl.t('pages.recentWriting.placementTest.table.headers.maps'),
        data: placementResults.map((d) => `${d.maps.from}–${d.maps.to}`),
      },
      {
        title: this.intl.t('pages.recentWriting.placementTest.table.headers.numberOfStudents'),
        data: placementResults.map((d) => (d.totalStudents === 0 ? '-' : String(d.totalStudents))),
      },
    ]
  }

  <template>
    {{#if this.fetchData.isPending}}
      <FidgetSpinnerWaveComponent @centered={{true}} />
    {{else}}
      {{#if (eq this.recentPlacementWritings.length 0)}}
        <span class="text-center italic">{{t "pages.recentWriting.noDataAvailable"}}</span>
      {{else}}
        <div class="mb-5 flex justify-end">
          <ReflectionCounter
            @smile={{this.reflectionCounts.smile}}
            @unsure={{this.reflectionCounts.unsure}}
            @disappointed={{this.reflectionCounts.disappointed}}
          />
        </div>

        <div class="mb-10">
          <span class="text-xl font-semibold">{{t "pages.recentWriting.placementTest.table.title"}}</span>
          <table class="my-2 w-full table-fixed border-2 shadow-lg">
            <tbody class="divide-dusty-black-100 divide-y">
              {{#each this.placementResultsTable as |tableRow|}}
                <tr>
                  <td class="text-dusty-black-200 w-1/2 p-2 uppercase">{{tableRow.title}}</td>
                  {{#each tableRow.data as |data|}}
                    <td class="w-1/3 p-2">{{data}}</td>
                  {{/each}}
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
        <ul class="space-y-3">
          {{#each this.recentPlacementWritings as |writing|}}
            <RecentWritingCardLesson
              @studentId={{writing.student_id}}
              @firstName={{writing.first_name}}
              @lastName={{writing.last_name}}
              @avatar={{writing.avatar}}
              @activityId={{writing.activity_id}}
              @activityTitle={{t "pages.recentWriting.placementTest.cardTitle"}}
              @studentAnswers={{writing.student_answers}}
              @createdDate={{writing.created_date}}
              @className={{writing.classroom_name}}
              @placedAt={{concat "Map " writing.map_number ", Lesson " writing.lesson_number}}
              @reflection={{writing.writing_mood}}
            />
          {{/each}}
        </ul>

        <BasePageSelector class="mt-8" @pageCount={{this.totalPages}} @itemCount={{this.latestData.total}} />
      {{/if}}
    {{/if}}
  </template>
}
